import { ChangeDetectionStrategy, Component, computed, OnInit, signal, ViewChild } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DateTime } from 'luxon';
import { MessageService, SortMeta } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { InputNumberModule } from 'primeng/inputnumber';
import { Table, TableModule } from 'primeng/table';
import { forkJoin } from 'rxjs';
import { FollowUpReportResults, RenewalReportResults, UiSafeStaff } from 'src/app/api';
import { LinkableLocationsService, ProfileLocation } from 'src/app/api/linkable-locations.service';
import { RecruitingService } from 'src/app/api/recruiting.service';
import { RenewalPaceGraphComponent } from 'src/app/app/routed-modules/recruiting/renewal-pace-graph/renewal-pace-graph.component';
import { ErrorService } from 'src/app/services/error.service';
import { DateRangePickerComponent, DateRangePresets } from 'src/app/ui/date-range-picker/date-range-picker.component';
import { SavedSearchComponent } from 'src/app/ui/saved-search/saved-search.component';
import { TableSkeletonComponent } from 'src/app/ui/skeletons/table-skeleton/table-skeleton.component';
import { StaffDropdownMultiComponent } from 'src/app/ui/staff-dropdown-multi/staff-dropdown-multi.component';
import { LocationMultiDropdownComponent } from 'src/app/ui/dropdowns/location-multi-dropdown/location-multi-dropdown.component';
import { Column, TableCaptionComponent, TableColumnsComponent, TableHeadersComponent } from 'src/app/ui/tables';
import { DateValidators } from 'src/app/ui/validators/dateValidators';
import { StaffV2Service } from 'src/app/api/staffV2.service';


interface TripTableData extends RenewalReportResults {
  renewed: boolean
}

interface OverallSummaryData {
  category: string,
  renewed: number,
  total: number,
  renewalPercent: number
}

interface LocationSummaryData {
  location: string,
  renewed: number,
  total: number,
  renewalPercent: number
}

interface TLSummaryData {
  tlName: string,
  renewed: number,
  total: number,
  renewalPercent: number
}

interface ConsultantSummaryData {
  consultantName: string,
  renewed: number,
  total: number,
  renewalPercent: number
}

interface FollowUpReportData extends FollowUpReportResults {
  callComplete: boolean
}

@Component({
  selector: 'app-renewal-report',
  imports: [LocationMultiDropdownComponent, StaffDropdownMultiComponent, RenewalPaceGraphComponent, TableCaptionComponent, TableSkeletonComponent, CardModule, TableModule, TableColumnsComponent, TableHeadersComponent, ReactiveFormsModule, SavedSearchComponent, DateRangePickerComponent, InputNumberModule],
  templateUrl: './renewal-report.component.html',
  styleUrl: './renewal-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RenewalReportComponent implements OnInit {


  constructor(
    private fb: FormBuilder,
    private recruitingService: RecruitingService,
    private messageService: MessageService,
    private errorService: ErrorService,
    private locationService: LinkableLocationsService,
    public staffService2: StaffV2Service
  ) {

  }

  @ViewChild('tripTable') tripTable!: Table;
  @ViewChild('summaryTable') summaryTable!: Table;
  @ViewChild('locationSummaryTable') locationSummaryTable!: Table;
  @ViewChild('tlSummaryTable') tlSummaryTable!: Table;
  @ViewChild('consultantSummaryTable') consultantSummaryTable!: Table;


  loading = signal(false)
  initialSearchComplete = signal(false)



  results = signal<RenewalReportResults[]>([])
  locations = signal<ProfileLocation[]>([])

  tripDatePresets: DateRangePresets[] = [
    { label: (DateTime.now().year - 1).toString(), value: [DateTime.now().minus({ years: 1 }).startOf('year').toJSDate(), DateTime.now().minus({ years: 1 }).endOf('year').toJSDate()] },
    { label: (DateTime.now().year).toString(), value: [DateTime.now().startOf('year').toJSDate(), DateTime.now().endOf('year').toJSDate()] },    
    
  ]

  renewalPresets: DateRangePresets[] = [
    { label: (DateTime.now().year).toString(), value: [DateTime.now().startOf('year').toJSDate(), DateTime.now().endOf('year').toJSDate()] },
    { label: (DateTime.now().year + 1).toString(), value: [DateTime.now().plus({ years: 1 }).startOf('year').toJSDate(), DateTime.now().plus({ years: 1 }).endOf('year').toJSDate()] },
    { label: (DateTime.now().year + 2).toString(), value: [DateTime.now().plus({ years: 2 }).startOf('year').toJSDate(), DateTime.now().plus({ years: 2 }).endOf('year').toJSDate()] },
  ]


  tripTableData = computed<TripTableData[]>(() => {
    const results = this.results()

    return results.sort((a, b) => a.pastTripDate?.getTime() - b.pastTripDate?.getTime()).map(result => {
      return {
        ...result,
        renewed: result.futureDate ? true : false
      }
    })
  })

  overallSummaryData = computed<OverallSummaryData[]>(() => {
    const results = this.results()

    const summaryData = new Map<string, OverallSummaryData>([
      ["all", { category: "All Trips", renewed: 0, total: 0, renewalPercent: 0 }],
      ['international', { category: "International Locations", renewed: 0, total: 0, renewalPercent: 0 }],
      ['domestic', { category: "Domestic Locations", renewed: 0, total: 0, renewalPercent: 0 }],
      ['usaCentral', { category: "USA-Central", renewed: 0, total: 0, renewalPercent: 0 }],
      ['usaCoastal', { category: "USA-Coastal", renewed: 0, total: 0, renewalPercent: 0 }],
      ['fullTime', { category: "Full Time TL", renewed: 0, total: 0, renewalPercent: 0 }],
      ['seasonalTl', { category: "Seasonal TL", renewed: 0, total: 0, renewalPercent: 0 }],
      ['mlTl', { category: "ML TL", renewed: 0, total: 0, renewalPercent: 0 }],
      ['other', { category: "Other", renewed: 0, total: 0, renewalPercent: 0 }]
    ]);

    const customerIdSet = new Set<number>()

    results.forEach(result => {

      //only count each group once to keep with the way the old report worked
      if (result.custId && !customerIdSet.has(result.custId)) {
        customerIdSet.add(result.custId)


        summaryData.get("all")!.total++
        if (result.futureDate) {
          summaryData.get("all")!.renewed++
        }

        switch (result.tripLeaderRole) {
          case "ft":
            summaryData.get("fullTime")!.total++
            if (result.futureDate) {
              summaryData.get("fullTime")!.renewed++
            }
            break
          case "pt":
            summaryData.get("seasonalTl")!.total++
            if (result.futureDate) {
              summaryData.get("seasonalTl")!.renewed++
            }
            break
          case "ml":
            summaryData.get("mlTl")!.total++
            if (result.futureDate) {
              summaryData.get("mlTl")!.renewed++
            }
            break
          case "other":
            summaryData.get("other")!.total++
            if (result.futureDate) {
              summaryData.get("other")!.renewed++
            }
            break
        }


        if (result.pastDomestic === 1) {
          summaryData.get("domestic")!.total++
          if (result.futureDate) {
            summaryData.get("domestic")!.renewed++
          }
        } else {
          summaryData.get("international")!.total++
          if (result.futureDate) {
            summaryData.get("international")!.renewed++
          }
        }

        if (result.region === "Central") {
          summaryData.get("usaCentral")!.total++;
          if (result.futureDate) {
            summaryData.get("usaCentral")!.renewed++;
          }
        } else if (result.region === "Coastal") {
          summaryData.get("usaCoastal")!.total++;
          if (result.futureDate) {
            summaryData.get("usaCoastal")!.renewed++;
          }
        }

      }

    })

    for (const [key, value] of summaryData.entries()) {
      if (value.total > 0) {
        value.renewalPercent = Math.round(value.renewed / value.total * 10000) / 100
      }

    }

    return Array.from(summaryData.values())
  })

  locationSummaryData = computed<LocationSummaryData[]>(() => {
    const results = this.results()


    const locationSummaryData = new Map<string, LocationSummaryData>()

    const customerIdSet = new Set<number>()

    results.forEach(result => {

      if (result.custId && !customerIdSet.has(result.custId)) {
        customerIdSet.add(result.custId)

        if (!locationSummaryData.has(result.pastLocationName)) {
          locationSummaryData.set(result.pastLocationName, {
            location: result.pastLocationName,
            renewed: 0,
            total: 0,
            renewalPercent: 0
          })
        }

        const location = locationSummaryData.get(result.pastLocationName)!


        location.total++
        if (result.futureDate) {
          location.renewed++
        }


      }
    })

    for (const [key, value] of locationSummaryData.entries()) {
      if (value.total > 0) {
        value.renewalPercent = Math.round(value.renewed / value.total * 10000) / 100
      }
    }

    return Array.from(locationSummaryData.values()).sort((a, b) => a.location.localeCompare(b.location))

  })

  tlSummaryData = computed<TLSummaryData[]>(() => {
    const results = this.results()

    const tlSummaryData = new Map<string, TLSummaryData>()

    const customerIdSet = new Set<number>()

    results.forEach(result => {

      if (result.custId && !customerIdSet.has(result.custId)) {
        customerIdSet.add(result.custId)

        if (result.tripLeaderName && !tlSummaryData.has(result.tripLeaderName)) {
          tlSummaryData.set(result.tripLeaderName, {
            tlName: result.tripLeaderName,

            renewed: 0,
            total: 0,
            renewalPercent: 0
          })
        }

        const tl = tlSummaryData.get(result.tripLeaderName ?? "")!

        //NOTE: If numbers aren't adding up, it could be because a trip had no TL assigned.  this check keeps us from crashing
        if (tl) {
          tl.total++
          if (result.futureDate) {
            tl.renewed++
          }
        }
      }

    })

    for (const [key, value] of tlSummaryData.entries()) {
      if (value.total > 0) {
        value.renewalPercent = Math.round(value.renewed / value.total * 10000) / 100
      }
    }

    return Array.from(tlSummaryData.values())

  })

  consultantSummaryData = computed<ConsultantSummaryData[]>(() => {
    const results = this.results()

    const consultantSummaryData = new Map<string, ConsultantSummaryData>()

    const customerIdSet = new Set<number>()

    results.forEach(result => {
      if (result.custId && !customerIdSet.has(result.custId)) {
        customerIdSet.add(result.custId)

        if (result.consultantName && !consultantSummaryData.has(result.consultantName)) {
          consultantSummaryData.set(result.consultantName, {
            consultantName: result.consultantName,
            renewed: 0,
            total: 0,
            renewalPercent: 0
          })
        }

        const consultant = consultantSummaryData.get(result.consultantName ?? "")!

        //NOTE: If numbers aren't adding up, it could be because a trip had no consultant assigned.  this check keeps us from crashing
        if (consultant) {
          consultant.total++
          if (result.futureDate) {
            consultant.renewed++
          }
        }
      }

    })

    for (const [key, value] of consultantSummaryData.entries()) {
      if (value.total > 0) {
        value.renewalPercent = Math.round(value.renewed / value.total * 10000) / 100
      }
    }

    return Array.from(consultantSummaryData.values())
  })

  followUpReportResults = signal<FollowUpReportResults[]>([])

  followUpReportData = computed<FollowUpReportData[]>(() => {
    const results = this.followUpReportResults()

    return results.map(result => {
      return {
        ...result,
        callComplete: result.percentComplete && result.percentComplete >= 80 ? true : false
      }
    })
  })

  defaultTripTableSort = signal<SortMeta[]>([{ field: "groupName", order: 1 }])

  tripTableColumns = signal<Column<TripTableData>[]>([
    { field: 'custId', header: "Group ID", type: "groupNameWithLink", custIdField: 'custId', sortable: true, filterable: true },
    { field: 'groupName', header: "Group Name", type: "groupNameWithLink", custIdField: 'custId', sortable: true, filterable: true },
    { field: 'pastTripDate', header: "Trip Date", type: "date", sortable: true, filterable: true },
    { field: 'pastLocationName', header: "Original Location", type: "text", sortable: true, filterable: true },
    { field: "tripLeaderName", header: "Original Trip Leader", type: "text", sortable: true, filterable: true },
    { field: "consultantName", header: "Original Consultant", type: "text", sortable: true, filterable: true },
    { field: "recruiterName", header: "Original Recruiter", type: "text", sortable: true, filterable: true },
    { field: "pastAttendees", header: "Original Size", type: "number", sortable: true, filterable: true },
    { field: "renewed", header: "Renewed", type: "boolean", severityMap: { true: "success", false: "danger" }, sortable: true, filterable: true },
    { field: "lastCallDate", header: "Last Call", type: "date", sortable: true, filterable: true },
    { field: "dateRenewed", header: "Date Renewed", type: "date", sortable: true, filterable: true },
    { field: "futureDate", header: "Future Trip Date", type: "date", sortable: true, filterable: true },
    { field: "renewalLocationName", header: "Future Location", type: "text", sortable: true, filterable: true },
    { field: "futureAttendees", header: "Future Size", type: "number", sortable: true, filterable: true },
  ])

  defaultSummaryTableSort = signal<SortMeta[]>([{ field: "category", order: 1 }])

  summaryColumns = signal<Column<OverallSummaryData>[]>([
    { field: "category", header: "Category", type: "text", sortable: true, filterable: true, clickeable: true, filterTableRef: 'tripTable' },
    { field: "renewed", header: "Renewed", type: "number", sortable: true, filterable: true },
    { field: "total", header: "Total", type: "number", sortable: true, filterable: true },
    { field: "renewalPercent", header: "Renewal Percent", type: "percentage", sortable: true, filterable: true },
  ])

  defaultLocationSummaryTableSort = signal<SortMeta[]>([{ field: "location", order: 1 }])

  locationSummaryColumns = signal<Column<LocationSummaryData>[]>([
    { field: "location", header: "Location", type: "text", sortable: true, filterable: true, clickeable: true, filterTableRef: 'tripTable', filterColumnIndex: 4, filterColumnField: "pastLocationName" },
    { field: "renewed", header: "Renewed", type: "number", sortable: true, filterable: true },
    { field: "total", header: "Total", type: "number", sortable: true, filterable: true },
    { field: "renewalPercent", header: "Renewal Percent", type: "percentage", sortable: true, filterable: true },
  ])

  defaultTLSummaryTableSort = signal<SortMeta[]>([{ field: "tlName", order: 1 }])

  tlSummaryColumns = signal<Column<TLSummaryData>[]>([
    { field: "tlName", header: "TL Name", type: "text", sortable: true, filterable: true, clickeable: true, filterTableRef: 'tripTable', filterColumnIndex: 5, filterColumnField: "tripLeaderName" },
    { field: "renewed", header: "Renewed", type: "number", sortable: true, filterable: true },
    { field: "total", header: "Total", type: "number", sortable: true, filterable: true },
    { field: "renewalPercent", header: "Renewal Percent", type: "percentage", sortable: true, filterable: true },
  ])

  defaultConsultantSummaryTableSort = signal<SortMeta[]>([{ field: "consultantName", order: 1 }])

  consultantSummaryColumns = signal<Column<ConsultantSummaryData>[]>([
    { field: "consultantName", header: "Consultant Name", type: "text", sortable: true, filterable: true, clickeable: true, filterTableRef: 'tripTable', filterColumnIndex: 6, filterColumnField: "consultantName" },
    { field: "renewed", header: "Renewed", type: "number", sortable: true, filterable: true },
    { field: "total", header: "Total", type: "number", sortable: true, filterable: true },
    { field: "renewalPercent", header: "Renewal Percent", type: "percentage", sortable: true, filterable: true },
  ])

  defaultFollowUpReportTableSort = signal<SortMeta[]>([{ field: "groupName", order: 1 }])

  followUpReportColumns = signal<Column<FollowUpReportData>[]>([
    { field: "custId", header: "Group ID", type: "groupNameWithLink", custIdField: 'custId', sortable: true, filterable: true },
    { field: "groupName", header: "Group Name", type: "groupNameWithLink", custIdField: 'custId', sortable: true, filterable: true },
    { field: "tripId", header: "Trip ID", type: "number", sortable: true, filterable: true },
    { field: "tripDate", header: "Trip Date", type: "date", sortable: true, filterable: true },
    { field: "callComplete", header: "Call Complete", type: "boolean", trueValue: "Complete", falseValue: "Incomplete", severityMap: { true: "success", false: "warn" }, sortable: true, filterable: true },
    { field: "attendees", header: "Attendees", type: "number", sortable: true, filterable: true },
    { field: "locationName", header: "Location", type: "text", sortable: true, filterable: true },
    { field: "consultantName", header: "Consultant", type: "text", sortable: true, filterable: true },
    { field: "tlName", header: "TL", type: "text", sortable: true, filterable: true },
  ])

  searchForm = this.fb.group({
    originalLocation: this.fb.nonNullable.control<ProfileLocation[]>([]),
    renewalLocation: this.fb.nonNullable.control<ProfileLocation[]>([]),
    tripLeaders: this.fb.control<UiSafeStaff[]>([]),
    consultants: this.fb.control<UiSafeStaff[]>([]),
    tripDateRange: this.fb.nonNullable.control<Date[]>([DateTime.now().startOf('year').toJSDate(), DateTime.now().startOf('day').toJSDate()], { validators: [DateValidators.dateRangeRequired] }),
    renewalDateRange: this.fb.nonNullable.control<Date[]>([DateTime.now().plus({ years: 1 }).startOf('year').toJSDate(), DateTime.now().plus({ years: 1 }).endOf('year').toJSDate()], { validators: [DateValidators.dateRangeRequired] }),
    minAttendees: this.fb.nonNullable.control<number>(0, [Validators.required, Validators.min(0)]),
    maxAttendees: this.fb.nonNullable.control<number>(9999, [Validators.required, Validators.min(0)])
  })



  paceReportYear = signal<number>(0)
  paceReportMinAttendees = signal<number>(0)
  paceReportMaxAttendees = signal<number>(9999)
  paceReportOriginalLocationIds = signal<string[]>([])
  paceReportTripLeaders = signal<number[]>([])
  paceReportConsultants = signal<number[]>([])

  ngOnInit(): void {
    this.locationService.getLocations("active").subscribe(locations => this.locations.set(locations))


  }

  search() {

    if (this.searchForm.valid) {
      this.loading.set(true)

      const rawValue = this.searchForm.getRawValue()
      this.paceReportYear.set(DateTime.fromJSDate(rawValue.tripDateRange[0]).year)
      this.paceReportMinAttendees.set(rawValue.minAttendees ?? 0)
      this.paceReportMaxAttendees.set(rawValue.maxAttendees ?? 9999)
      this.paceReportOriginalLocationIds.set(rawValue.originalLocation.map(l => l.id))
      this.paceReportTripLeaders.set(rawValue.tripLeaders?.map(l => l.id) ?? [])
      this.paceReportConsultants.set(rawValue.consultants?.map(l => l.id) ?? [])

      forkJoin([

        this.recruitingService.getRenewalReport({
          tripStartDate: rawValue.tripDateRange[0],
          tripEndDate: rawValue.tripDateRange[1],
          renewalStartDate: rawValue.renewalDateRange[0],
          renewalEndDate: rawValue.renewalDateRange[1],
          minAttendees: rawValue.minAttendees ?? 0,
          maxAttendees: rawValue.maxAttendees ?? 9999,
          originalLocationIds: rawValue.originalLocation.map(l => l.id),
          renewalLocationIds: rawValue.renewalLocation.map(l => l.id),
          tripLeaders: rawValue.tripLeaders?.map(l => l.id) ?? [],
          consultants: rawValue.consultants?.map(l => l.id) ?? []
        }),
        this.recruitingService.getFollowUpReportData({
          tripStartDate: rawValue.tripDateRange[0],
          tripEndDate: rawValue.tripDateRange[1],
          minAttendees: rawValue.minAttendees ?? 0,
          maxAttendees: rawValue.maxAttendees ?? 9999,
          originalLocationIds: rawValue.originalLocation.map(l => l.id),
          tripLeaders: rawValue.tripLeaders?.map(l => l.id) ?? [],
          consultants: rawValue.consultants?.map(l => l.id) ?? []
        })
      ]).subscribe({
        next: ([renewalResults, followUpResults]) => {
          this.results.set(renewalResults)
          this.followUpReportResults.set(followUpResults)
          this.initialSearchComplete.set(true)
          this.loading.set(false)
        },
        error: (err) => {
          this.errorService.error("There was an error loading the renewal report", err, this.messageService)
          this.loading.set(false)
        }
      })

    }
  }

  filterTeamByTeamTable(event: { columnField: string, filterValue: any, columnIndex: number | undefined, targetTable: string | undefined, filterColumnFieldId?: string }) {

    if (this.tripTable && event.filterValue) {
      this.tripTable.clear();

      if (event.columnField === "category") {
        switch (event.filterValue) {
          case "All Trips":
            break;
          case "Domestic Locations":
            this.tripTable.filter(1, 'pastDomestic', 'equals');
            break;
          case "International Locations":
            this.tripTable.filter(0, 'pastDomestic', 'equals');
            break;
          case "Full Time TL":
            this.tripTable.filter("ft", 'tripLeaderRole', 'equals');
            break;
          case "Seasonal TL":
            this.tripTable.filter("pt", 'tripLeaderRole', 'equals');
            break;
          case "ML TL":
            this.tripTable.filter("ml", 'tripLeaderRole', 'equals');
            break;
          case "USA-Central":
            this.tripTable.filter("Central", 'region', 'equals');
            break;
          case "USA-Coastal":
            this.tripTable.filter("Coastal", 'region', 'equals');
            break;
          default:
        }
      }

      else if (event.columnField === "location") {
        if (event.filterColumnFieldId) {
          this.tripTable.filter(event.filterValue, event.filterColumnFieldId, 'contains');
        }
      }
      else if (event.columnField === "tlName") {
        if (event.filterColumnFieldId) {
          this.tripTable.filter(event.filterValue, event.filterColumnFieldId, 'contains');
        }
      }
      else if (event.columnField === "consultantName") {
        if (event.filterColumnFieldId) {
          this.tripTable.filter(event.filterValue, event.filterColumnFieldId, 'contains');
        }
      }
      else if (event.filterColumnFieldId) {
        this.tripTable.filter(event.filterValue, event.filterColumnFieldId, 'contains');
      } else {
        this.tripTable.filter(event.filterValue, event.columnField, 'contains');
      }

    }
  }

  clearAllFilters() {
    if (this.tripTable) {
      this.tripTable.clear();
    }
  }

}
